import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  bprsAssessments: [],
  currentBprsAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all BPRS Assessments (optional patient)
export const fetchBprsAssessments = createAsyncThunk(
  'bprsAssessments/fetchBprsAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/bprs-assessments?patient_id=${patientId}`
        : '/bprs-assessments';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch BPRS assessments'
      );
    }
  }
);

// Fetch single BPRS Assessment
export const fetchBprsAssessment = createAsyncThunk(
  'bprsAssessments/fetchBprsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/bprs-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch BPRS assessment'
      );
    }
  }
);

// Create BPRS Assessment
export const createBprsAssessment = createAsyncThunk(
  'bprsAssessments/createBprsAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/bprs-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create BPRS assessment'
      );
    }
  }
);

// Update BPRS Assessment
export const updateBprsAssessment = createAsyncThunk(
  'bprsAssessments/updateBprsAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/bprs-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update BPRS assessment'
      );
    }
  }
);

// Delete BPRS Assessment
export const deleteBprsAssessment = createAsyncThunk(
  'bprsAssessments/deleteBprsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/bprs-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete BPRS assessment'
      );
    }
  }
);

// ==================== Slice ====================
const bprsAssessmentsSlice = createSlice({
  name: 'bprsAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBprsAssessment: (state, action) => {
      state.currentBprsAssessment = action.payload;
    },
    clearCurrentBprsAssessment: (state) => {
      state.currentBprsAssessment = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchBprsAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBprsAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bprsAssessments = action.payload;
      })
      .addCase(fetchBprsAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchBprsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBprsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBprsAssessment = action.payload;
      })
      .addCase(fetchBprsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createBprsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBprsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bprsAssessments.unshift(action.payload);
      })
      .addCase(createBprsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateBprsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBprsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.bprsAssessments.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.bprsAssessments[index] = updated;
        }

        if (state.currentBprsAssessment?.id === updated.id) {
          state.currentBprsAssessment = updated;
        }
      })
      .addCase(updateBprsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteBprsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBprsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        state.bprsAssessments = state.bprsAssessments.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentBprsAssessment?.id === action.payload) {
          state.currentBprsAssessment = null;
        }
      })
      .addCase(deleteBprsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentBprsAssessment,
  clearCurrentBprsAssessment,
} = bprsAssessmentsSlice.actions;

export default bprsAssessmentsSlice.reducer;
